import { z } from 'zod';

import { timestampSchema, uuidSchema } from '../general';
import { addressSchema, bigIntSchema } from '../web3';

import { entityLabelSchema } from './misc';
import { employeeStockPlanSchema } from './stocks';
import { evmTokenSchema, genericTokenSchema, otcTokenSchema } from './tokens';

// --- Transfer Receipts ---

const baseTransferReceiptSchema = z.object({
  /** Unique identifier of the transfer */
  id: uuidSchema,
  /** Source entity of the transfer */
  from: entityLabelSchema,
  /** Destination entity of the transfer */
  to: entityLabelSchema,
  /** Total amount of the transfer in USDC, before any fees are deducted */
  totalAmountUsdc: z.string(),
  /** The fee that Endaoment charges for the transfer in USDC */
  endaomentFeeUsdc: z.string(),
  /** The net amount of the transfer in USDC, after all fees are deducted */
  proceedsUsdc: z.string(),
  /** The date and time when this transfer was created, in UTC */
  createdAtDateUtc: z.string(),
});

export const asyncTransferReceiptStatusSchema = z.enum(['PendingLiquidation', 'Liquidated', 'Cancelled', 'ClawedBack']);
export type AsyncTransferReceiptStatus = z.infer<typeof asyncTransferReceiptStatusSchema>;

export const asyncTransferReceiptSchema = baseTransferReceiptSchema.extend({
  /** Status of the transfer */
  status: asyncTransferReceiptStatusSchema,
  /** The date and time when this transfer was completed successfully, in UTC. Null if the transfer is not completed. */
  completedAtDateUtc: z.string().nullable(),
  /** The ID of the transfer settled on chain, if any. Null if the transfer is not completed. */
  completedTransferId: uuidSchema.nullable(),
  /** The transaction hash of the transfer settled on chain, if any. Null if the transfer is not completed. */
  completedTransactionHash: addressSchema.nullable(),
});
export type AsyncTransferReceipt = z.infer<typeof asyncTransferReceiptSchema>;

export const transferReceiptStatusSchema = z.enum(['Liquidated', 'ClawedBack']);
export type TransferReceiptStatus = z.infer<typeof transferReceiptStatusSchema>;

export const transferReceiptSchema = baseTransferReceiptSchema.extend({
  /** Status of the transfer */
  status: transferReceiptStatusSchema,
  /** The date and time when this transfer was completed successfully, in UTC */
  completedAtDateUtc: z.string(),
  /** The transaction hash of the transfer settled on chain */
  completedTransactionHash: addressSchema,
});
export type TransferReceipt = z.infer<typeof transferReceiptSchema>;

// --- Onchain/EVM Donation Receipt ---

export const onchainDonationReceiptSchema = z.object({
  /** Unique identifier of the donation */
  id: uuidSchema,
  /** Organization or fund receiving the donation */
  donatingTo: entityLabelSchema,
  /** Token of this donation */
  token: evmTokenSchema,
  /** Input amount of the donation, in the smallest currency unit of the donated token */
  inputAmount: bigIntSchema,
  /** Amount donated, in USDC, before any Endaoment fees are deducted. This is how much USDC Endaoment got for the given asset, after all liquidation fees are deducted (for example, swap provider fees). */
  usdcAmount: z.string(),
  /** Endaoment platform fees in USDC, in the smallest currency unit */
  endaomentFeesUsdc: z.string(),
  /** Net proceeds to the organization after all fees in USDC, in the smallest currency unit */
  proceedsUsdc: z.string(),
  /** Date and time when the donation was initiated, in UTC */
  initiatedAtDateUtc: z.string(),
  /** Current status of the donation */
  status: z.enum(['Completed']),
  /** Date and time when the donation was completed, in UTC */
  completedAtDateUtc: z.string(),
  /** Transaction hash on the blockchain for verification */
  transactionHash: addressSchema,
  /** Chain ID of the Donation */
  chainId: z.number(),
  /** URL to the transaction on the blockchain */
  transactionUrl: z.string(),
});
export type OnchainDonationReceipt = z.infer<typeof onchainDonationReceiptSchema>;

// --- Donation Pledge Receipts ---

export const pledgeOutcomeSchema = z.enum(['Pending', 'AwaitingAssets', 'OnRamping', 'Success', 'Failure']);
export type PledgeOutcome = z.infer<typeof pledgeOutcomeSchema>;

const baseDonationPledgeReceiptSchema = z.object({
  id: uuidSchema,
  outcome: pledgeOutcomeSchema,
  destinationEntity: entityLabelSchema,
  netDonationUsdc: bigIntSchema.nullish(),
  protocolFeesUsdc: bigIntSchema.nullish(),
  /** The date and time the pledge was initiated. */
  initiatedAtUtc: timestampSchema,
  /** The date and time the pledge was completed. */
  completedAtUtc: timestampSchema.nullish(),
});
export const cryptoDonationPledgeReceiptSchema = baseDonationPledgeReceiptSchema.extend({
  type: z.literal('CryptoDonationPledge'),
  inputAmount: bigIntSchema,
  inputToken: z.union([otcTokenSchema, evmTokenSchema, genericTokenSchema]), // Keep genericTokenSchema last to avoid match on loose `type`
  otcTransactionHash: z.string().nullish(),
  centralizedExchangeDonationStatus: z.enum(['Pending', 'Completed', 'Failed']).nullish(),
  centralizedExchangeTransactionId: z.string().nullish(),
});
export type CryptoDonationPledgeReceipt = z.infer<typeof cryptoDonationPledgeReceiptSchema>;

export const stockDonationPledgeReceiptSchema = baseDonationPledgeReceiptSchema.extend({
  type: z.literal('StockDonationPledge'),
  inputStock: z.object({ name: z.string(), ticker: z.string() }),
  shares: z.number({ coerce: true }), // Coerce to number since BE returns a string
  lots: z.array(
    z.object({
      numberOfShares: z.number(),
      purchasePrice: z.number(),
      purchaseDate: z.string().transform(v => {
        // Returned as an unnecessary datetime
        const date = v.split('T')[0];
        return date || v;
      }),
      employeeStockPlan: employeeStockPlanSchema.nullish(),
      lotId: z.string().nullish(),
    }),
  ),
});

export type StockDonationPledgeReceipt = z.infer<typeof stockDonationPledgeReceiptSchema>;

export const stripePaymentTypeSchema = z.enum([
  'Card',
  'Bank Transfer',
  'Apple Pay',
  'Cash App',
  'Google Pay',
  'Other',
]);
export type StripePaymentType = z.infer<typeof stripePaymentTypeSchema>;
export const stripeDonationPledgeReceiptSchema = baseDonationPledgeReceiptSchema.extend({
  type: z.literal('StripeDonationPledge'),
  stripePaymentIntentId: z.string(),
  selectedPaymentMethod: stripePaymentTypeSchema.nullish(),
  pledgedAmountCents: z.number(),
  stripeFeesCents: z.number().nullish(),
});
export type StripeDonationPledgeReceipt = z.infer<typeof stripeDonationPledgeReceiptSchema>;

// Includes DAF migration pledges
export const cashDonationPledgeReceiptSchema = baseDonationPledgeReceiptSchema.extend({
  type: z.literal('CashDonationPledge'),
  reason: z.enum(['DafMigration', 'WirePledge']),
});
export type CashDonationPledgeReceipt = z.infer<typeof cashDonationPledgeReceiptSchema>;

export const custodianCashPledgeReceiptSchema = baseDonationPledgeReceiptSchema.extend({
  type: z.literal('CustodianCashPledge'),
  custodianName: z.string(),
});
export type CustodianCashPledgeReceipt = z.infer<typeof custodianCashPledgeReceiptSchema>;

export const donationPledgeReceiptSchema = z.union([
  cryptoDonationPledgeReceiptSchema,
  stripeDonationPledgeReceiptSchema,
  cashDonationPledgeReceiptSchema,
  custodianCashPledgeReceiptSchema,
  stockDonationPledgeReceiptSchema,
]);
export type DonationPledgeReceipt = z.infer<typeof donationPledgeReceiptSchema>;
